import { Editor } from '@tiptap/core';
import { Attrs, Node } from '@tiptap/pm/model';
import { NodeSelection, TextSelection, Selection } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import { JSONContent } from '@tiptap/react';

export const getNodesOfType = (content: JSONContent, type: string[] | string): JSONContent[] => {
  return (
    content?.content?.reduce<JSONContent[]>((foundNodes, node) => {
      if ((typeof type === 'string' && type === node.type) || (typeof type === 'object' && type.includes(node.type ?? ''))) {
        return [...foundNodes, node, ...getNodesOfType(node, type)];
      }
      if (node.content) {
        const nestedNodes = getNodesOfType(node, type);
        return [...foundNodes, ...nestedNodes];
      }
      return foundNodes;
    }, []) ?? []
  );
};

export const getTextsWithMarks = (content: JSONContent, markType: string): JSONContent[] => {
  const textNodes = getNodesOfType(content, 'text');
  return textNodes.filter((node) => {
    const marks = node.marks ?? [];
    return marks.some((mark) => mark.type === markType);
  });
};

export const getText = (content: JSONContent): string => {
  let initialText = content.text ?? '';
  const texts = getNodesOfType(content, 'text')
    .filter((node) => !!node.text)
    .map((node) => node.text);
  return initialText + texts.join('');
};

export const findTextOccurrences = (doc: Node, searchText: string): { from: number; to: number }[] => {
  const positions: { from: number; to: number }[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    let index = node.text.indexOf(searchText);
    while (index !== -1) {
      positions.push({ from: pos + index, to: pos + index + searchText.length });
      index = node.text.indexOf(searchText, index + 1);
    }
  });
  return positions;
};

const findNodePos = (doc: Node, node: Node): number | null => {
  let result = null;
  doc.descendants((descendant, pos) => {
    if (descendant === node) {
      result = pos;
      return false; // Stop the iteration
    }
  });
  return result;
};

export const updateNodeAttributes = (editor: Editor, node: Node, newAttributes: Attrs) => {
  const { state, view } = editor;
  const { tr } = state;
  const pos = findNodePos(state.doc, node);
  if (pos !== null) {
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      ...newAttributes,
    });
    view.dispatch(tr);
  }
};

export const updateAllMatchingNodeAttributes = (editor: Editor, type: string, predicate: (attrs: Attrs) => boolean, newAttributes: Attrs) => {
  const { state, view } = editor;
  const { tr } = state;

  state.doc.descendants((node, pos) => {
    if (node.type.name === type && predicate(node.attrs)) {
      tr.setNodeMarkup(pos, node.type, {
        ...node.attrs,
        ...newAttributes,
      });
    }
  });

  if (tr.docChanged) {
    view.dispatch(tr);
  }
};

export const getSelectedNodeAttributes = <TAttrs>(selection: Selection): TAttrs | TAttrs[] | null => {
  if (!selection) return null;
  if (selection instanceof CellSelection) {
    const nodes = selection.ranges.map((r) => r.$from.node());
    const attrs = nodes.map((n) => n.attrs);
    if (attrs.length === 1) {
      return attrs[0] as TAttrs;
    }
    return attrs as TAttrs[];
  }
  if (selection instanceof NodeSelection) {
    return selection.node.attrs as TAttrs;
  }
  if (selection instanceof TextSelection) {
    const { $from } = selection;
    const parentNode = $from.node(-1);
    return parentNode.attrs as TAttrs;
  }
  return null;
};

export const getSelectedText = (editor: Editor) => {
  const { state } = editor;
  let { from, to } = state.selection;
  return state.doc.textBetween(from, to);
};
